import { supabase, supabaseAdmin } from '../config/supabase';
import { v4 as uuidv4 } from 'uuid';
import slugify from '../utils/slugify';

export interface Profession {
  id: number;
  name: string;
  description?: string;
  category?: string;
}

export interface Trader {
  id?: string;
  user_id: string | null;  
  email: string;
  store_name: string;
  slug?: string;
  owner_name: string;
  location: string;
  description: string;
  phone_number: string;
  profession_id: string;
  profession?: Profession;
  secondary_profession_id?: number | string | null;
  secondary_profession?: Profession | null;
  avatar_url?: string;
  website?: string;
  rating: number;
  reviews: number;
  verified: boolean;
  mockup: boolean; // Indicates if this is a demo/development entry
  show_map?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TraderFeatures {
  user_id: string;
  feature1_enabled?: boolean;
  feature1_title?: string;
  feature1_description?: string;
  feature1_image?: string;

  feature2_enabled?: boolean;
  feature2_title?: string;
  feature2_description?: string;
  feature2_image?: string;

  feature3_enabled?: boolean;
  feature3_title?: string;
  feature3_description?: string;
  feature3_image?: string;

  features_section_title?: string;
  features_layout?: 'rows' | 'columns';

  created_at?: string;
  updated_at?: string;
}

export const traderService = {
  generateUniqueSlug: async (storeName: string, excludeId?: string | number): Promise<string> => {
    const base = slugify(storeName);
    let candidate = base || `trader-${Date.now()}`;
    let suffix = 2;

    while (true) {
      let query = supabase
        .from('traders')
        .select('id')
        .eq('slug', candidate);

      if (excludeId !== undefined && excludeId !== null) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query.limit(1);

      if (error) {
        console.error('Error checking slug uniqueness:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return candidate;
      }

      candidate = `${base}-${suffix++}`;
    }
  },
  // Upload avatar for a trader
  uploadAvatar: async (traderId: string | number, file: File): Promise<string> => {
    try {
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${traderId}_${uuidv4()}.${fileExt}`;
      
      // Use a consistent path format
      const filePath = `avatars/traders/${traderId}/${fileName}`;

      // Upload file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Error uploading avatar:', uploadError);
        throw uploadError;
      }

      // Get public URL for the uploaded file
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (!data || !data.publicUrl) {
        const urlError = new Error('Error getting avatar URL: publicUrl is null');
        console.error(urlError.message);
        throw urlError;
      }

      const publicUrl = data.publicUrl;

      return publicUrl;
    } catch (error) {
      console.error('Unexpected error in uploadAvatar:', error);
      throw error;
    }
  },

  getTraderBySlug: async (slug: string): Promise<(Trader & { trader_features?: TraderFeatures }) | null> => {
    try {
      if (!slug) {
        console.error('Invalid trader slug: slug is required');
        return null;
      }

      const { data: trader, error: traderError } = await supabase
        .from('traders')
        .select(`
          *,
          profession:professions!traders_profession_id_fkey(*),
          secondary_profession:professions!traders_secondary_profession_id_fkey(*)
        `)
        .eq('slug', slug)
        .single();

      if (traderError) {
        console.error('Error fetching trader by slug:', traderError);
        return null;
      }

      const userId = trader?.user_id;
      let features: TraderFeatures | null = null;
      if (userId) {
        features = await traderService.getTraderFeatures(userId);
      } else {
        console.warn('Trader found by slug but user_id is missing; skipping features load:', trader);
      }

      const defaultFeatures: TraderFeatures = {
        user_id: userId || '',
        feature1_enabled: true,
        feature1_title: trader?.store_name || 'Our Business',
        feature1_description: trader?.description || 'Discover our unique offerings',
        feature1_image: trader?.avatar_url || '/bally-Market-sign-200px.png',
        feature2_enabled: false,
        feature2_title: '',
        feature2_description: '',
        feature2_image: '',
        feature3_enabled: false,
        feature3_title: '',
        feature3_description: '',
        feature3_image: '',
        features_section_title: 'Featured Offerings',
        features_layout: 'rows'
      };

      const traderFeatures: TraderFeatures = {
        ...defaultFeatures,
        ...(features || {}),
        features_section_title:
          (features?.features_section_title?.trim()) || defaultFeatures.features_section_title,
        features_layout:
          features?.features_layout === 'columns' ? 'columns' : defaultFeatures.features_layout
      };

      return {
        ...trader,
        trader_features: traderFeatures
      };
    } catch (error) {
      console.error('Unexpected error in getTraderBySlug:', error);
      return null;
    }
  },

  // Get all traders
  getAllTraders: async (): Promise<(Trader & { trader_features?: TraderFeatures })[]> => {
    try {
      // First, fetch traders with their profession
      const { data: traders, error: tradersError } = await supabase
        .from('traders')
        .select(`
          *,
          profession:professions!traders_profession_id_fkey(*),
          secondary_profession:professions!traders_secondary_profession_id_fkey(*)
        `)
        .or('verified.eq.true,mockup.eq.true')
        .order('store_name', { ascending: true });

      if (tradersError) {
        console.error('Error fetching traders:', tradersError);
        return [];
      }

      // If no traders, return early
      if (!traders || traders.length === 0) {
        return [];
      }

      // Filter out traders with null user_id
      const validTraders = traders.filter((trader): trader is Trader & { user_id: string } => Boolean(trader.user_id));

      // Fetch features for valid traders
      const userIds = validTraders.map(trader => trader.user_id);
      let features: TraderFeatures[] = [];

      if (userIds.length > 0) {
        try {
          const featuresQuery = await supabase
            .from('trader_features')
            .select('*')
            .in('user_id', userIds);

          if (featuresQuery.error) {
            console.warn('Error fetching trader features:', featuresQuery.error);
          }

          features = featuresQuery.data || [];
        } catch (featuresQueryError) {
          console.error('Unexpected error in features query:', featuresQueryError);
        }
      }

      // Merge traders with their features, creating default features based on trader data if none found
      const processedTraders = traders.map(trader => {
        const defaultFeatures: TraderFeatures = {
          user_id: trader.user_id || '',
          feature1_enabled: true,
          feature1_title: trader?.store_name || 'Our Business',
          feature1_description: trader?.description || 'Discover our unique offerings',
          feature1_image: trader?.avatar_url || '/bally-Market-sign-200px.png',
          feature2_enabled: false,
          feature2_title: '',
          feature2_description: '',
          feature2_image: '',
          feature3_enabled: false,
          feature3_title: '',
          feature3_description: '',
          feature3_image: '',
          features_section_title: 'Featured Offerings',
          features_layout: 'rows'
        };

        if (!trader.user_id) {
          return {
            ...trader,
            trader_features: defaultFeatures
          };
        }

        const traderFeatures: TraderFeatures = {
          ...defaultFeatures,
          ...(features.find(f => f.user_id === trader.user_id) || {}),
          features_section_title:
            (features.find(f => f.user_id === trader.user_id)?.features_section_title?.trim()) ||
            defaultFeatures.features_section_title,
          features_layout:
            (features.find(f => f.user_id === trader.user_id)?.features_layout === 'columns')
              ? 'columns'
              : defaultFeatures.features_layout
        };

        return {
          ...trader,
          trader_features: traderFeatures
        };
      });

      return processedTraders;
    } catch (error) {
      console.error('Unexpected error in getAllTraders:', error);
      return [];
    }
  },

  // Get trader by ID with features
  getTraderById: async (id: string): Promise<(Trader & { trader_features?: TraderFeatures }) | null> => {
    try {
      // Validate input
      if (!id) {
        console.error('Invalid trader ID: ID is required');
        return null;
      }

      // Fetch trader with profession details
      const { data: trader, error: traderError } = await supabase
        .from('traders')
        .select(`
          *,
          profession:professions!traders_profession_id_fkey(*),
          secondary_profession:professions!traders_secondary_profession_id_fkey(*)
        `)
        .eq('id', id)
        .single();

      if (traderError) {
        console.error('Error fetching trader:', traderError);
        return null;
      }

      // Validate trader and user_id
      if (!trader) {
        console.error('No trader found with the given ID');
        return null;
      }

      const userId = trader.user_id;

      if (!userId) {
        console.warn('Trader found but user_id is missing; skipping features load:', trader);
      }

      console.log('Trader fetched:', trader);
      console.log('Trader user_id:', userId);

      // Fetch trader features via shared helper for consistent behaviour
      let features: TraderFeatures | null = null;
      if (userId) {
        features = await traderService.getTraderFeatures(userId);
      }

      const defaultFeatures: TraderFeatures = {
        user_id: userId || '',
        feature1_enabled: true,
        feature1_title: trader?.store_name || 'Our Business',
        feature1_description: trader?.description || 'Discover our unique offerings',
        feature1_image: trader?.avatar_url || '/bally-Market-sign-200px.png',
        feature2_enabled: false,
        feature2_title: '',
        feature2_description: '',
        feature2_image: '',
        feature3_enabled: false,
        feature3_title: '',
        feature3_description: '',
        feature3_image: '',
        features_section_title: 'Featured Offerings',
        features_layout: 'rows'
      };

      const traderFeatures: TraderFeatures = {
        ...defaultFeatures,
        ...(features || {}),
        features_section_title:
          (features?.features_section_title?.trim()) || defaultFeatures.features_section_title,
        features_layout:
          features?.features_layout === 'columns' ? 'columns' : defaultFeatures.features_layout
      };

      // Merge trader with features
      const traderWithFeatures = {
        ...trader,
        trader_features: traderFeatures
      };

      console.log('Trader with features:', traderWithFeatures);

      return traderWithFeatures;
    } catch (error) {
      console.error('Unexpected error in getTraderById:', error);
      // Log the full error details
      console.error('Full unexpected error:', JSON.stringify(error, null, 2));
      return null;
    }
  },

  // Get trader by user ID
  getTraderByUserId: async (userId: string): Promise<Trader | null> => {
    const { data, error } = await supabase
      .from('traders')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching trader by user ID:', error);
      return null;
    }

    return data;
  },

  // Get trader features
  getTraderFeatures: async (userId: string): Promise<TraderFeatures | null> => {
    console.log('Attempting to fetch trader features for user:', userId);
    
    try {
      // Fetch features using the user_id, without .single()
      const { data, error } = await supabase
        .from('trader_features')
        .select('*')
        .eq('user_id', userId);

      console.log('Supabase query result:', { 
        data, 
        error, 
        userId,
        dataLength: data?.length
      });

      if (error) {
        console.error('Error fetching trader features:', error);
        return null;
      }

      // Return the first record if exists
      return data.length > 0 ? data[0] : null;
    } catch (err) {
      console.error('Unexpected error in getTraderFeatures:', err);
      return null;
    }
  },

  // Update trader features
  // Set skipAuthCheck to true when calling from admin panel
  updateTraderFeatures: async (userId: string, updates: Partial<TraderFeatures>, skipAuthCheck: boolean = false) => {
    console.log('Attempting to update trader features:', { 
      userId, 
      updates,
      skipAuthCheck
    });

    try {
      // Choose client: normal supabase for traders, supabaseAdmin for admin operations
      const client = skipAuthCheck ? supabaseAdmin : supabase;

      // Verify the current user matches the user_id (only for non-admin paths)
      if (!skipAuthCheck) {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user || user.id !== userId) {
          throw new Error('Unauthorized: User ID mismatch');
        }
      }

      // Prepare the update data with explicit fields
      const updateData: Partial<TraderFeatures> = {};
      
      // Explicitly map the updates to prevent any unexpected fields
      const updateKeys = [
        'feature1_enabled', 'feature2_enabled', 'feature3_enabled',
        'feature1_title', 'feature2_title', 'feature3_title',
        'feature1_description', 'feature2_description', 'feature3_description',
        'feature1_image', 'feature2_image', 'feature3_image',
        'features_section_title'
      ];

      updateKeys.forEach(key => {
        const value = updates[key as keyof TraderFeatures];
        if (value !== undefined) {
          if (key === 'features_section_title' && typeof value === 'string') {
            (updateData as any)[key] = value.trim() || 'Featured Offerings';
          } else {
            (updateData as any)[key] = value;
          }
        }
      });

      if (updates.features_layout !== undefined) {
        updateData.features_layout = updates.features_layout === 'columns' ? 'columns' : 'rows';
      }

      // Add timestamp for tracking
      updateData.updated_at = new Date().toISOString();

      console.log('Prepared update data:', updateData);

      // First, check if a record exists
      const { data: existingRecord, error: fetchError } = await client
        .from('trader_features')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Unexpected error checking trader_features existence:', fetchError);
        throw fetchError;
      }

      if (fetchError?.code === 'PGRST116' || !existingRecord) {
        // If no record exists, create a new one
        const { data: newRecord, error: insertError } = await client
          .from('trader_features')
          .insert({
            user_id: userId,
            ...updateData,
            // Default values for any missing fields
            feature1_enabled: updateData.feature1_enabled ?? true,
            feature2_enabled: updateData.feature2_enabled ?? false,
            feature3_enabled: updateData.feature3_enabled ?? false,
            feature1_title: updateData.feature1_title ?? 'My Business',
            feature2_title: updateData.feature2_title ?? 'Orders',
            feature3_title: updateData.feature3_title ?? 'Analytics',
            feature1_description: updateData.feature1_description ?? 'Discover our unique offerings',
            feature2_description: updateData.feature2_description ?? 'View and manage your orders',
            feature3_description: updateData.feature3_description ?? 'View your sales and performance analytics',
            feature1_image: updateData.feature1_image ?? '/bally-Market-sign-200px.png',
            feature2_image: updateData.feature2_image ?? '/bally-Market-sign-200px.png',
            feature3_image: updateData.feature3_image ?? '/bally-Market-sign-200px.png'
          })
          .select()
          .maybeSingle();

        if (insertError) {
          console.error('Error creating trader features:', insertError);
          throw insertError;
        }

        console.log('Created new trader features record:', newRecord);
        return newRecord;
      }

      // Update existing record
      const { data, error } = await client
        .from('trader_features')
        .update(updateData)
        .eq('user_id', userId)
        .select()
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error updating trader features:', error);
        throw error;
      }

      console.log('Updated trader features record:', data || existingRecord);
      return data || existingRecord;
    } catch (error) {
      console.error('Comprehensive error in updateTraderFeatures:', error);
      throw error;
    }
  },

  // Add new trader
  addTrader: async (trader: Omit<Trader, 'id'>): Promise<string | null> => {
    const slug = await traderService.generateUniqueSlug(trader.store_name);

    const { data, error } = await supabase
      .from('traders')
      .insert([{
        ...trader,
        slug,
        user_id: (await supabase.auth.getUser()).data.user?.id  // Get current user's ID
      }])
      .select()
      .single();

    if (error) {
      console.error('Error adding trader:', error);
      return null;
    }

    return data?.id;
  },

  // Update trader
  updateTrader: async (id: string | number, trader: Partial<Trader>): Promise<Trader | null> => {
    try {
      // Log the update attempt
      console.log('🔄 Attempting to update trader:', { id, updateData: trader });
      
      // Get current user to verify permissions
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current authenticated user:', user?.id);
      
      // Get current trader data to check user_id
      const { data: currentTrader } = await supabase
        .from('traders')
        .select('user_id')
        .eq('id', id)
        .single();
      console.log('Trader user_id:', currentTrader?.user_id);
      
      const updates = { ...trader } as Partial<Trader>;

      if (trader.store_name) {
        updates.slug = await traderService.generateUniqueSlug(trader.store_name, id);
      }

      const { data, error } = await supabase
        .from('traders')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single();

      if (error) {
        console.error('❌ Error updating trader:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }
      
      console.log('✅ Trader updated successfully:', data);

      // If updating description, store_name, or location, trigger keyword regeneration
      const keywordRelevantFields = ['description', 'store_name', 'location'];
      const updatedKeywordFields = keywordRelevantFields.some(field => trader[field as keyof Trader] !== undefined);
      
      if (updatedKeywordFields && data) {
        console.log('🔄 Triggering keyword regeneration for trader:', data.id);
        console.log('Business data:', {
          store_name: data.store_name,
          description: data.description,
          location: data.location
        });
        
        // Import dynamically to avoid circular dependencies
        import('./keywordService').then(({ triggerKeywordGeneration }) => {
          triggerKeywordGeneration(data.id, 'trader', {
            store_name: data.store_name,
            description: data.description,
            location: data.location
          });
        }).catch(err => {
          console.warn('⚠️ Failed to trigger keyword generation:', err);
        });
      }

      return data;
    } catch (error) {
      console.error('Unexpected error in updateTrader:', error);
      return null;
    }
  },

  // Delete trader
  deleteTrader: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('traders')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting trader:', error);
    }
  }
};
